import { useMemo, useState } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    exportCSCustomerMovementsExcel,
    getCSCustomerMovements,
} from '@/modules/Report/services'
import { SalesTargetFilters } from '../../../BusinessTargets/types'
import { Button, Typogaphy } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    CSCustomerMovementDataQuality,
    CSCustomerMovementDataQualityIssue,
    CSCustomerMovementMetric,
    CSCustomerMovementRow,
} from '@/modules/Report/types'
import { AlertTriangle, Download } from 'lucide-react'
import toast from 'react-hot-toast'

interface CustomerMovementsProps {
    filters: SalesTargetFilters
}

const actionLabels = {
    new_sale: 'Bán mới',
    upsell: 'Bán thêm',
    renewal: 'Duy trì',
    cancel: 'Hủy dịch vụ',
}

type MovementAction = keyof Pick<
    CSCustomerMovementRow,
    'new_sale' | 'upsell' | 'renewal' | 'cancel'
>

type CSCustomerMovementSummaryRow = Omit<CSCustomerMovementRow, 'month'> & {
    period: string
}

export default function CustomerMovements({ filters }: CustomerMovementsProps) {
    const { t } = useTranslation('report')
    const [isExporting, setIsExporting] = useState(false)

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_TARGETS_ANALYTICS_CUSTOMER_MOVEMENTS,
            filters.employee_id,
            filters.product_group_name,
            filters.gte,
            filters.lte,
        ],
        queryFn: () => getCSCustomerMovements(filters),
        enabled: !!filters.gte && !!filters.lte,
        placeholderData: true,
    })

    const rows = useMemo(
        () => buildSummaryRows(data?.rows ?? [], filters),
        [data?.rows, filters]
    )
    const dataQuality = data?.data_quality
    const hasDataQualityWarning =
        !!dataQuality &&
        (dataQuality.cancel_missing_date_count > 0 ||
            dataQuality.cancel_missing_end_date_count > 0 ||
            dataQuality.cancel_missing_recurring_price_count > 0 ||
            dataQuality.cancel_missing_statement_history_count > 0)

    const handleExport = async () => {
        if (!filters.gte || !filters.lte) return

        try {
            setIsExporting(true)
            const response = await exportCSCustomerMovementsExcel(filters)
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = getExportFilename(response.headers, filters)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success(t('export_success'))
        } catch {
            toast.error(t('export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    const columns: DataTableColumn<CSCustomerMovementSummaryRow>[] = useMemo(
        () => [
            {
                key: 'period',
                title: 'Kỳ báo cáo',
                width: 170,
                sortable: true,
                pinned: 'left',
            },
            {
                key: 'cs_employee_name',
                title: 'CS',
                width: 220,
                pinned: 'left',
                render: (value: string, row) => (
                    <div className="flex flex-col">
                        <Typogaphy className="font-medium">
                            {value || '-'}
                        </Typogaphy>
                        <span className="text-xs text-gray-500">
                            #{row.cs_employee_id}
                        </span>
                    </div>
                ),
            },
            metricCustomerColumn('new_sale', actionLabels.new_sale),
            metricValueColumn('new_sale', 'Giá trị bán mới'),
            metricServicesColumn('new_sale', 'Dịch vụ bán mới'),
            metricCustomerColumn('upsell', actionLabels.upsell),
            metricValueColumn('upsell', 'Giá trị bán thêm'),
            metricServicesColumn('upsell', 'Dịch vụ bán thêm'),
            metricCustomerColumn('renewal', actionLabels.renewal),
            metricValueColumn('renewal', 'Giá trị duy trì'),
            metricServicesColumn('renewal', 'Dịch vụ duy trì'),
            metricCustomerColumn('cancel', actionLabels.cancel),
            metricValueColumn('cancel', 'Lost revenue'),
            metricServicesColumn('cancel', 'Dịch vụ hủy'),
        ],
        []
    )

    const stateComponent = renderState()

    return (
        <div className="flex flex-col">
            <Paper className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <Typogaphy className="font-semibold">
                            Báo cáo khách hàng theo CS
                        </Typogaphy>
                        <div className="mt-1 text-sm text-gray-500">
                            {filters.gte} - {filters.lte}
                        </div>
                    </div>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<Download className="h-4 w-4" />}
                        onClick={handleExport}
                        disabled={!filters.gte || !filters.lte || isExporting}
                    >
                        {isExporting ? t('downloading') : t('download_excel')}
                    </Button>
                </div>

                {data?.grand_total && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <SummaryBlock
                            label={actionLabels.new_sale}
                            metric={data.grand_total.new_sale}
                        />
                        <SummaryBlock
                            label={actionLabels.upsell}
                            metric={data.grand_total.upsell}
                        />
                        <SummaryBlock
                            label={actionLabels.renewal}
                            metric={data.grand_total.renewal}
                        />
                        <SummaryBlock
                            label={actionLabels.cancel}
                            metric={data.grand_total.cancel}
                        />
                    </div>
                )}

                {hasDataQualityWarning && dataQuality && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <div className="flex flex-col gap-1">
                            {getDataQualityIssues(dataQuality).map((issue) => (
                                <div key={issue.issue}>
                                    <span className="font-medium">
                                        {dataQualityIssueLabel(issue.issue)}:{' '}
                                        {issue.count}
                                    </span>
                                    {issue.service_ids.length > 0 && (
                                        <span className="ml-2 text-amber-700">
                                            service_id:{' '}
                                            {serviceIDsPreview(
                                                issue.service_ids
                                            )}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {stateComponent || (
                    <DataTableCustomized<CSCustomerMovementSummaryRow>
                        columns={columns}
                        rows={rows}
                        rowKey={(row) => row.cs_employee_id}
                        size="small"
                        bordered
                        autoWidth
                        stickyHeader
                        maxHeight="calc(100vh - 340px)"
                        sorting={{
                            defaultSortKey: 'cs_employee_name',
                            defaultSortOrder: 'asc',
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}

function SummaryBlock({
    label,
    metric,
}: {
    label: string
    metric: CSCustomerMovementMetric
}) {
    return (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="text-xs font-medium uppercase text-gray-500">
                {label}
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
                {formatMoney(metric.total_value)}
            </div>
            <div className="mt-1 text-xs text-gray-500">
                {metric.customer_count} KH · {sumServices(metric.services)} DV
            </div>
        </div>
    )
}

function metricCustomerColumn(
    action: MovementAction,
    title: string
): DataTableColumn<CSCustomerMovementSummaryRow> {
    return {
        key: `${action}_customers`,
        title: `${title} KH`,
        width: 120,
        align: 'center',
        valueGetter: (row) => row[action].customer_count,
        render: (value: number) => value || '-',
    }
}

function metricValueColumn(
    action: MovementAction,
    title: string
): DataTableColumn<CSCustomerMovementSummaryRow> {
    return {
        key: `${action}_value`,
        title,
        width: 180,
        align: 'right',
        valueGetter: (row) => row[action].total_value,
        render: (value: number) => (value ? formatMoney(value) : '-'),
    }
}

function metricServicesColumn(
    action: MovementAction,
    title: string
): DataTableColumn<CSCustomerMovementSummaryRow> {
    return {
        key: `${action}_services`,
        title,
        width: 260,
        valueGetter: (row) => row[action].services,
        render: (value: Record<string, number>) => servicesToText(value),
    }
}

function formatMoney(value: number) {
    return new Intl.NumberFormat('vi-VN', {
        maximumFractionDigits: 0,
    }).format(value)
}

function sumServices(services: Record<string, number>) {
    return Object.values(services ?? {}).reduce((sum, value) => sum + value, 0)
}

function servicesToText(services: Record<string, number>) {
    const entries = Object.entries(services ?? {}).filter(([, count]) => count)
    if (!entries.length) return '-'

    return entries
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, count]) => `${name}: ${count}`)
        .join(', ')
}

function getDataQualityIssues(
    dataQuality: CSCustomerMovementDataQuality
): CSCustomerMovementDataQualityIssue[] {
    if (dataQuality.issues?.length) {
        return dataQuality.issues.filter((issue) => issue.count > 0)
    }

    return [
        {
            issue: 'cancel_missing_date',
            count: dataQuality.cancel_missing_date_count,
            service_ids: dataQuality.cancel_missing_date_service_ids ?? [],
        },
        {
            issue: 'cancel_missing_end_date',
            count: dataQuality.cancel_missing_end_date_count,
            service_ids: dataQuality.cancel_missing_end_date_service_ids ?? [],
        },
        {
            issue: 'cancel_missing_recurring_price',
            count: dataQuality.cancel_missing_recurring_price_count,
            service_ids:
                dataQuality.cancel_missing_recurring_price_service_ids ?? [],
        },
        {
            issue: 'cancel_missing_statement_history',
            count: dataQuality.cancel_missing_statement_history_count,
            service_ids:
                dataQuality.cancel_missing_statement_history_service_ids ?? [],
        },
    ].filter((issue) => issue.count > 0)
}

function dataQualityIssueLabel(issue: string) {
    switch (issue) {
        case 'cancel_missing_date':
            return 'Thiếu ngày hủy'
        case 'cancel_missing_end_date':
            return 'Thiếu ngày hết hạn'
        case 'cancel_missing_recurring_price':
            return 'Thiếu recurring price'
        case 'cancel_missing_statement_history':
            return 'Thiếu lịch sử statement 6 tháng'
        default:
            return issue
    }
}

function serviceIDsPreview(serviceIDs: number[]) {
    const previewLimit = 12
    const preview = serviceIDs.slice(0, previewLimit).join(', ')
    if (serviceIDs.length <= previewLimit) {
        return preview
    }
    return `${preview}, ... +${serviceIDs.length - previewLimit}`
}

function buildSummaryRows(
    rows: CSCustomerMovementRow[],
    filters: SalesTargetFilters
): CSCustomerMovementSummaryRow[] {
    const period = formatPeriod(filters)
    const rowMap = new Map<number, CSCustomerMovementSummaryRow>()

    rows.forEach((row) => {
        const existing = rowMap.get(row.cs_employee_id)
        if (!existing) {
            rowMap.set(row.cs_employee_id, {
                cs_employee_id: row.cs_employee_id,
                cs_employee_name: row.cs_employee_name,
                period,
                new_sale: cloneMetric(row.new_sale),
                upsell: cloneMetric(row.upsell),
                renewal: cloneMetric(row.renewal),
                cancel: cloneMetric(row.cancel),
            })
            return
        }

        mergeMetric(existing.new_sale, row.new_sale)
        mergeMetric(existing.upsell, row.upsell)
        mergeMetric(existing.renewal, row.renewal)
        mergeMetric(existing.cancel, row.cancel)
    })

    return Array.from(rowMap.values()).sort((a, b) => {
        if (a.cs_employee_name !== b.cs_employee_name) {
            return a.cs_employee_name.localeCompare(b.cs_employee_name)
        }
        return a.cs_employee_id - b.cs_employee_id
    })
}

function cloneMetric(
    metric: CSCustomerMovementMetric
): CSCustomerMovementMetric {
    return {
        customer_count: metric.customer_count,
        total_value: metric.total_value,
        services: { ...metric.services },
    }
}

function mergeMetric(
    target: CSCustomerMovementMetric,
    source: CSCustomerMovementMetric
) {
    target.customer_count += source.customer_count
    target.total_value += source.total_value
    Object.entries(source.services ?? {}).forEach(([service, count]) => {
        target.services[service] = (target.services[service] ?? 0) + count
    })
}

function formatPeriod(filters: SalesTargetFilters) {
    if (filters.gte && filters.lte) {
        return `${filters.gte} - ${filters.lte}`
    }
    return '-'
}

function getExportFilename(
    headers: Record<string, unknown>,
    filters: SalesTargetFilters
) {
    const disposition =
        typeof headers?.['content-disposition'] === 'string'
            ? headers['content-disposition']
            : ''
    const match = disposition.match(/filename="?([^"]+)"?/i)
    return (
        match?.[1] ??
        `cs_customer_movements_${filters.gte ?? 'from'}_${filters.lte ?? 'to'}.xlsx`
    )
}
